// Renders one AI proposal as a reviewable diff. Applying sends only immutable
// operation/change indexes; the server projects the stored proposal before write.

import { useState, type ReactNode } from "react";
import { Columns2, Rows2 } from "lucide-react";
import {
  parseProposalRef,
  type AiItemChange,
  type AiProposalOp,
  type AiUndoState,
} from "@checklist/shared";
import { ApiError, applyProposal, discardProposal, type ApiApplyProposalResult } from "../../lib/api.js";
import { Button } from "../ui/Button.js";
import { SelectableSuggestionRow, SuggestionSelectionToolbar } from "./SelectableSuggestion.js";
import { collectPreviewGroups, ProposalPreview } from "./ProposalPreview.js";
import { GROUP_HEADING_CLASS } from "./groupHeadingStyle.js";
import {
  allSelection,
  atomicKey,
  changeKey,
  initialSelection,
  makeRequest,
  onlySelectedAdditions,
  selectionCount,
  toggleChoice,
  totalChoices,
  type SelectionKey,
} from "./proposalSelection.js";

export interface ProposalCardProps {
  proposalId: string;
  summary: string;
  ops: AiProposalOp[];
  onApplied: (result: ApiApplyProposalResult, selectedCount: number) => void;
  onDiscarded: () => void;
  undoState?: AiUndoState;
  onUndo?: () => void | Promise<void>;
  onRedo?: () => void | Promise<void>;
}

function DiffLine({ sign, children }: { sign: "+" | "-" | "~"; children: ReactNode }) {
  const cls =
    sign === "+" ? "text-success" : sign === "-" ? "text-danger line-through" : "text-warning";
  return (
    <div className={`flex gap-2 text-sm ${cls}`}>
      <span aria-hidden="true" className="w-3 shrink-0 font-mono font-semibold">
        {sign}
      </span>
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}

/** A group/section name within an op's body — deliberately louder than the item
 * rows below it (uppercase, letter-spaced, brand color, rule) so a scan down the
 * proposal makes group boundaries obvious at a glance. Matches ProposalPreview's
 * section headers so the diff list and the side-by-side view read as one system. */
function GroupHeading({ children }: { children: ReactNode }) {
  return <div className={GROUP_HEADING_CLASS}>{children}</div>;
}

function OpBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-line bg-slate-50 p-3">
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function itemChangeDetail(change: AiItemChange): ReactNode {
  if (change.op === "add") return <>{change.text}{change.group ? ` (in ${change.group})` : ""}</>;
  if (change.op === "edit") return <>{change.text}{change.newText ? ` -> ${change.newText}` : ""}</>;
  return change.text;
}

function itemChangeSign(change: AiItemChange): "+" | "-" | "~" {
  return change.op === "add" ? "+" : change.op === "remove" ? "-" : "~";
}

function opTitle(op: AiProposalOp): string {
  switch (op.op) {
    case "items_change":
      return "Change items";
    case "grouping":
      return op.section
        ? `Reorganize ${op.target.kind} into groups (${op.section})`
        : `Reorganize ${op.target.kind} into groups`;
    case "metadata":
      return "Update details";
    case "new_list":
      return `New list: ${op.name}`;
    case "new_template":
      return `New template: ${op.name}`;
    case "new_checklist":
      return `New checklist: ${op.name}`;
    case "check_state":
      return "Check off items";
  }
}

/**
 * A list/template reference as the user should read it: "$ref:N" names the new
 * list or template created earlier on this same card; a literal id means one the
 * user already has (its raw id means nothing to them).
 */
function referenceLabel(value: string, ops: AiProposalOp[], kind: "list" | "template"): string {
  const index = parseProposalRef(value);
  if (index === null) return `your existing ${kind}`;
  const target = ops[index];
  return target && "name" in target && target.name ? `new ${kind} "${target.name}"` : `a new ${kind}`;
}

function renderOpBody(op: AiProposalOp, ops: AiProposalOp[]): ReactNode {
  switch (op.op) {
    case "items_change":
      return op.changes.map((change, changeIndex) => (
        <DiffLine key={changeIndex} sign={itemChangeSign(change)}>
          {itemChangeDetail(change)}
        </DiffLine>
      ));
    case "grouping":
      return (
        <>
          {op.subgroups.map((group, groupIndex) => (
            <div key={groupIndex}>
              <GroupHeading>{group.name}</GroupHeading>
              {group.items.map((text, itemIndex) => (
                <DiffLine key={itemIndex} sign="~">
                  {text}
                </DiffLine>
              ))}
            </div>
          ))}
          {op.ungrouped.length ? (
            <div>
              <GroupHeading>Ungrouped</GroupHeading>
              {op.ungrouped.map((text, itemIndex) => (
                <DiffLine key={itemIndex} sign="~">
                  {text}
                </DiffLine>
              ))}
            </div>
          ) : null}
        </>
      );
    case "metadata":
      return (
        <>
          {op.name !== undefined ? <DiffLine sign="~">Name: {op.name}</DiffLine> : null}
          {op.description !== undefined ? (
            <DiffLine sign="~">Description: {op.description}</DiffLine>
          ) : null}
          {op.tags !== undefined ? (
            <DiffLine sign="~">Tags: {op.tags.join(", ") || "(none)"}</DiffLine>
          ) : null}
        </>
      );
    case "new_list":
      return (
        <>
          {op.items.map((text, itemIndex) => (
            <DiffLine key={`i${itemIndex}`} sign="+">
              {text}
            </DiffLine>
          ))}
          {op.subgroups.map((group, groupIndex) => (
            <div key={`g${groupIndex}`}>
              <GroupHeading>{group.name}</GroupHeading>
              {group.items.map((text, itemIndex) => (
                <DiffLine key={itemIndex} sign="+">
                  {text}
                </DiffLine>
              ))}
            </div>
          ))}
        </>
      );
    case "new_template":
      return op.sections.map((section, sectionIndex) =>
        section.type === "list" ? (
          <DiffLine key={sectionIndex} sign="+">
            Section from {referenceLabel(section.listId, ops, "list")}
          </DiffLine>
        ) : (
          <div key={sectionIndex}>
            <GroupHeading>{section.name}</GroupHeading>
            {section.items.map((text, itemIndex) => (
              <DiffLine key={itemIndex} sign="+">
                {text}
              </DiffLine>
            ))}
          </div>
        )
      );
    case "new_checklist":
      return op.templateId ? (
        <DiffLine sign="+">From {referenceLabel(op.templateId, ops, "template")}</DiffLine>
      ) : (
        (op.sections ?? []).map((section, sectionIndex) => (
          <div key={sectionIndex}>
            <GroupHeading>{section.name}</GroupHeading>
            {section.items.map((text, itemIndex) => (
              <DiffLine key={itemIndex} sign="+">
                {text}
              </DiffLine>
            ))}
          </div>
        ))
      );
    case "check_state":
      return (
        <>
          {op.checked.map((text, itemIndex) => (
            <DiffLine key={`c${itemIndex}`} sign="+">
              {text}
            </DiffLine>
          ))}
          {op.unchecked.map((text, itemIndex) => (
            <DiffLine key={`u${itemIndex}`} sign="-">
              {text}
            </DiffLine>
          ))}
        </>
      );
  }
}

function renderStaticOp(op: AiProposalOp, index: number, ops: AiProposalOp[]): ReactNode {
  return (
    <OpBlock key={index} title={opTitle(op)}>
      {renderOpBody(op, ops)}
    </OpBlock>
  );
}

function renderSelectableOp(
  op: AiProposalOp,
  ops: AiProposalOp[],
  opIndex: number,
  selected: Set<SelectionKey>,
  setSelected: (key: SelectionKey, checked: boolean) => void,
  disabled: boolean
): ReactNode {
  if (op.op === "items_change") {
    return (
      <div key={opIndex}>
        <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
          Change items
        </div>
        <div className="space-y-1">
          {op.changes.map((change, changeIndex) => {
            const key = changeKey(opIndex, changeIndex);
            const tone = change.op === "edit" ? "change" : change.op;
            return (
              <SelectableSuggestionRow
                key={key}
                id={`proposal-${opIndex}-change-${changeIndex}`}
                checked={selected.has(key)}
                disabled={disabled}
                onCheckedChange={(checked) => setSelected(key, checked)}
                tone={tone}
                label={`Apply ${change.op}: ${change.text}`}
              >
                <DiffLine sign={itemChangeSign(change)}>{itemChangeDetail(change)}</DiffLine>
              </SelectableSuggestionRow>
            );
          })}
        </div>
      </div>
    );
  }

  const key = atomicKey(opIndex);
  return (
    <SelectableSuggestionRow
      key={key}
      id={`proposal-${opIndex}`}
      checked={selected.has(key)}
      disabled={disabled}
      onCheckedChange={(checked) => setSelected(key, checked)}
      tone={op.op.startsWith("new_") ? "add" : "change"}
      label={`Apply ${op.op.replaceAll("_", " ")}`}
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">{opTitle(op)}</div>
      <div className="mt-1.5 space-y-1">{renderOpBody(op, ops)}</div>
    </SelectableSuggestionRow>
  );
}

export function ProposalCard({
  proposalId,
  summary,
  ops,
  onApplied,
  onDiscarded,
  undoState,
  onUndo,
  onRedo,
}: ProposalCardProps) {
  const [busy, setBusy] = useState<"apply" | "discard" | "undo" | "redo" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<"applied" | "discarded" | null>(null);
  const [selected, setSelected] = useState<Set<SelectionKey>>(() => initialSelection(ops));
  const [viewMode, setViewMode] = useState<"diff" | "side-by-side">("diff");
  const selectedCount = selectionCount(ops, selected);
  const choiceCount = totalChoices(ops);
  const additionsOnly = onlySelectedAdditions(ops, selected);
  const canPreview = !undoState && collectPreviewGroups(ops).length > 0;

  function setChoice(key: SelectionKey, checked: boolean) {
    setSelected((previous) => toggleChoice(ops, previous, key, checked));
  }

  async function handleApply() {
    setBusy("apply");
    setError(null);
    try {
      const result = await applyProposal(proposalId, makeRequest(ops, selected));
      setDone("applied");
      onApplied(result, selectedCount);
    } catch (err) {
      if (err instanceof ApiError && err.code === "PROPOSAL_STALE") {
        setError("This changed since the suggestion — ask again.");
      } else if (err instanceof ApiError && err.code === "PROPOSAL_DEPENDENCY_UNSELECTED") {
        setError("A template or checklist you selected needs the new list or template it is built from - select that too.");
      } else if (err instanceof ApiError && err.code === "PROPOSAL_REF_UNRESOLVED") {
        setError("Part of this suggestion depends on something that was not created - ask again.");
      } else if (err instanceof ApiError && err.code === "PROPOSAL_ALREADY_APPLIED") {
        setDone("applied");
        onApplied({ proposalId, applied: 0, results: [] }, selectedCount);
      } else {
        setError(err instanceof Error ? err.message : "Could not apply this.");
      }
    } finally {
      setBusy(null);
    }
  }

  async function handleDiscard() {
    setBusy("discard");
    setError(null);
    try {
      await discardProposal(proposalId);
      setDone("discarded");
      onDiscarded();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not discard this.");
    } finally {
      setBusy(null);
    }
  }

  async function handleUndoRedo() {
    const action = undoState === "applied" ? "undo" : "redo";
    setBusy(action);
    setError(null);
    try {
      await (action === "undo" ? onUndo?.() : onRedo?.());
    } catch (err) {
      setError(err instanceof Error ? err.message : `Could not ${action} these changes.`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50/40 p-3">
      <div className="text-sm font-semibold text-ink">{summary}</div>
      {!undoState ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <div className="flex-1">
            <SuggestionSelectionToolbar
              selectedCount={selectedCount}
              totalCount={choiceCount}
              disabled={busy !== null}
              onSelectAll={() => setSelected(allSelection(ops))}
              onClear={() => setSelected(new Set())}
            />
          </div>
          {canPreview ? (
            <div
              role="radiogroup"
              aria-label="Proposal view"
              className="hidden shrink-0 items-center gap-0.5 rounded-lg border border-line bg-white p-0.5 sm:flex"
            >
              <button
                type="button"
                role="radio"
                aria-checked={viewMode === "diff"}
                title="Diff list"
                onClick={() => setViewMode("diff")}
                className={
                  "flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition " +
                  (viewMode === "diff" ? "bg-brand-600 text-white" : "text-muted hover:bg-slate-100 hover:text-ink")
                }
              >
                <Rows2 size={14} aria-hidden="true" />
                Diff
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={viewMode === "side-by-side"}
                title="Side by side"
                onClick={() => setViewMode("side-by-side")}
                className={
                  "flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition " +
                  (viewMode === "side-by-side"
                    ? "bg-brand-600 text-white"
                    : "text-muted hover:bg-slate-100 hover:text-ink")
                }
              >
                <Columns2 size={14} aria-hidden="true" />
                Side by side
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      {!undoState && canPreview && viewMode === "side-by-side" ? (
        <div className="mt-2">
          <ProposalPreview ops={ops} selected={selected} />
        </div>
      ) : null}
      <div className="mt-2 space-y-2">
        {ops.map((op, index) =>
          undoState
            ? renderStaticOp(op, index, ops)
            : renderSelectableOp(op, ops, index, selected, setChoice, busy !== null)
        )}
      </div>
      {error ? <div className="mt-2 text-sm text-danger">{error}</div> : null}
      {undoState ? (
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => void handleUndoRedo()} disabled={busy !== null}>
            {busy === "undo"
              ? "Undoing…"
              : busy === "redo"
                ? "Redoing…"
                : undoState === "applied"
                  ? "Undo changes"
                  : "Redo changes"}
          </Button>
        </div>
      ) : done ? (
        <div className="mt-3 text-sm font-medium text-muted">
          {done === "applied" ? "Applied." : "Discarded."}
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <Button size="sm" onClick={handleApply} disabled={busy !== null || selectedCount === 0}>
            {busy === "apply"
              ? "Applying…"
              : additionsOnly
                ? `Add ${selectedCount} selected`
                : `Apply ${selectedCount} selected changes`}
          </Button>
          <Button size="sm" variant="secondary" onClick={handleDiscard} disabled={busy !== null}>
            {busy === "discard" ? "Discarding…" : "Discard"}
          </Button>
        </div>
      )}
    </div>
  );
}
